/**
 * verdictTranslator — pure function that turns a review-agent
 * verdict + scorecards into a Variant C ``PendingApproval`` (or null
 * when the verdict doesn't warrant interrupting the writer).
 *
 * G-P2-A path (c): the backend has no native ``section.needs_approval``
 * SSE event. Instead, we treat the persona-review verdict as the
 * approval signal — when any persona returns major_revisions or
 * refuse_to_file, the spine flips to ``approval`` and the popout
 * surfaces the most-severe issue's anchor for the writer's call.
 *
 * SRP: pure function. No state, no I/O. Same input → same output.
 *
 * Tier mapping (verdict → popout tier → visual treatment):
 *   pass           → null (no popout; section stays drafted)
 *   minor_revisions→ null (advisory; doesn't pause the writer)
 *   major_revisions→ 'block' (must address; amber border)
 *   refuse_to_file → 'high-stakes' (regulatory blocker; red border)
 *
 * Question + context excerpt: derived from the most-severe issue
 * across all personas. "Most severe" = critical > major > minor.
 * Ties broken by first-issue-wins for determinism.
 */

import type { PersonaIssue, PersonaScorecard, Severity, Verdict } from "../components/review-agent/types";
import type { VerdictRecord } from "./hooks/useRunVerdictWatcher";
import type { PendingApproval } from "./state";

// ---------------------------------------------------------------------------
// Spec 23 P3 — Quality signals for the spine's enriched sections.
//
// Two cheap surfacings on drafted spine rows: a 3-value quality tag
// (high/medium/low) and a flagged-count (``N⚐``). Both are pure
// derivations over a VerdictRecord. Lives here (not in LoomSpine or
// the shell) because this module already owns the "verdict →
// Variant-C surface" translation; adding more of that logic keeps
// the mapping together.
// ---------------------------------------------------------------------------

/**
 * Map the sidecar's 4-value verdict enum to the spine's 3-value
 * quality tag. Only applied to sections the sidecar has verdicts
 * for — sections without a verdict keep their quality field
 * undefined (spine renders no badge).
 */
export function verdictToQuality(verdict: Verdict): "high" | "medium" | "low" {
  switch (verdict) {
    case "pass":
      return "high";
    case "minor_revisions":
      return "medium";
    case "major_revisions":
    case "refuse_to_file":
      return "low";
    default:
      return assertUnreachableVerdict(verdict);
  }
}

function assertUnreachableVerdict(v: never): never {
  throw new Error(`[verdictTranslator] unhandled Verdict: ${String(v)}`);
}

/**
 * Count unresolved findings across every persona in the verdict
 * record. Used by the spine's ``N⚐`` flag badge (Spec 22 P6).
 * Returns 0 when no scorecards are present — the section still
 * renders, just without a flag badge.
 */
export function countFindings(record: VerdictRecord): number {
  return record.scorecards.reduce((acc, sc) => acc + sc.issues.length, 0);
}

export interface QualitySignals {
  quality: "high" | "medium" | "low";
  flaggedCount: number;
}

/** Combined derivation — one call per section-with-verdict. Returns
 *  the subset of SpineSection fields the shell merges in. */
export function deriveQualitySignals(record: VerdictRecord): QualitySignals {
  return {
    quality: verdictToQuality(record.verdict),
    flaggedCount: countFindings(record),
  };
}

// ---------------------------------------------------------------------------
// Severity ordering
// ---------------------------------------------------------------------------

const SEVERITY_RANK: Record<Severity, number> = {
  critical: 3,
  major: 2,
  minor: 1,
};

/** Pick the most-severe issue across all scorecards. Returns null
 *  when no issues exist. */
function pickMostSevereIssue(scorecards: PersonaScorecard[]): PersonaIssue | null {
  let best: PersonaIssue | null = null;
  let bestRank = 0;
  for (const sc of scorecards) {
    for (const issue of sc.issues ?? []) {
      const rank = SEVERITY_RANK[issue.severity] ?? 0;
      if (rank > bestRank) {
        best = issue;
        bestRank = rank;
      }
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Verdict → tier mapping
// ---------------------------------------------------------------------------

export type ApprovalTier = "info" | "block" | "high-stakes";

/** Translate a verdict to its UI tier, or null when the verdict
 *  doesn't warrant a popout. */
function verdictToTier(verdict: Verdict): ApprovalTier | null {
  switch (verdict) {
    case "refuse_to_file":
      return "high-stakes";
    case "major_revisions":
      return "block";
    // Pass + minor_revisions don't pause the writer. Minor advisories
    // are visible in the Inspector's Review tab; no need to surface
    // them as a blocking popout.
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface TranslateInput {
  /** Section the verdict applies to. */
  sectionId: string;
  /** Display number for the popout header (e.g. "9.2"). */
  sectionNumber: string;
  /** Aggregated verdict (from AuditRecord.aggregated_verdict). */
  verdict: Verdict;
  /** Per-persona scorecards (from ReviewResponse.scorecards). May be
   *  empty when the audit has landed but the scorecards haven't yet. */
  scorecards: PersonaScorecard[];
}

/**
 * Translate a review verdict into a PendingApproval ready for
 * dispatch to the reducer. Returns null when the verdict doesn't
 * call for an approval interruption.
 *
 * When scorecards are empty (audit landed before scorecards), the
 * popout still surfaces with a generic "verdict says X" message —
 * the writer can drill into the Review tab for details. This
 * preserves the principle that the verdict is the trigger, not the
 * scorecards.
 */
export function translateVerdictToApproval(input: TranslateInput): PendingApproval | null {
  const tier = verdictToTier(input.verdict);
  if (!tier) return null;

  const issue = pickMostSevereIssue(input.scorecards);
  const verdictLabel =
    input.verdict === "refuse_to_file"
      ? "Refuse to file"
      : input.verdict === "major_revisions"
        ? "Major revisions"
        : input.verdict;

  const question = issue?.description ?? `Reviewer verdict: ${verdictLabel}. Open the Review tab for details.`;
  const contextExcerpt = issue?.draft_excerpt ?? "";

  return {
    sectionId: input.sectionId,
    sectionNumber: input.sectionNumber,
    tier,
    question,
    contextExcerpt,
  };
}
